/**
 * This is a pretty important component (after Login). How you set this up will
 * determine how the client speaks to the Fabric3 RPC server.
 *
 * That is to say, this the only place where the tRPC Client library is
 * configured for all Fabric3 Apps. So PLEASE BE CAREFUL! Read the docs, know
 * what you're doing, and don't just do things because you saw some 'cool shit'
 * in a Medium post or a HackerNews comment.
 */

import { RPC_SERVER_ENDPOINTS } from "@granular/fabric3-definitions";
import { globalAlerts } from "@granular/gds";
import { type Query, QueryCache, QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import {
  httpBatchLink,
  httpLink,
  loggerLink,
  splitLink,
} from "@trpc/react-query";
import React, { useMemo } from "react";
import superjson from "superjson";
import { addRumTiming } from "../helpers/datadog";
import {
  createOriginalIDBPersister,
  createRobustIDBPersister,
} from "../helpers/QueryIdbPersister";
import { trpc } from "../services/trpc";
import { useAuth } from "../services/auth/useAuth";
import { useFeatureFlags } from "../hooks/featureFlags";

/**
 * Canonical augmentation of React Query's per-query `meta`. This is the single
 * place it is declared for the whole workspace — do not re-declare it per app.
 *
 * These fields are the supported replacement for the per-query `onSuccess` /
 * `onError` callbacks (removed from `useQuery` in TanStack Query v5). They are
 * read by the shared {@link queryCache} `onError` handler below.
 */
declare module "@tanstack/react-query" {
  /**
   * TanStack v5 derives `QueryMeta` from `Register`, so the typed shape is
   * registered there rather than by augmenting `QueryMeta` directly.
   */
  interface Register {
    queryMeta: {
      /**
       * When set, the shared QueryCache `onError` fires a global danger alert with
       * this message if the query errors. Opt-in per query.
       */
      errorMessage?: string;
      /**
       * Escape hatch for structured per-query error side effects (e.g. auth
       * gating, reducer dispatch). Invoked by the shared QueryCache `onError`
       * exactly once per settle — the same timing the removed `onError` query
       * option had. Prefer deriving from query state in render where possible.
       */
      onError?: (
        error: unknown,
        query: Query<unknown, unknown, unknown, readonly unknown[]>,
      ) => void;
    };
  }
}

/**
 * The single, shared QueryCache for the whole shell + all federated apps.
 *
 * Its `onError` is the centralized query-error channel (TanStack's recommended
 * replacement for per-query `onError`): it fires a global alert when a query
 * opts in via `meta.errorMessage`, and invokes `meta.onError` for structured
 * per-query side effects. `globalAlerts` is GDS's imperative, non-hook accessor
 * (safe no-op if no alert provider has mounted yet), which is required here
 * because the cache is constructed at import time, outside React.
 */
export const queryCache = new QueryCache({
  onError: (error, query) => {
    if (error instanceof Error && query.meta?.errorMessage) {
      globalAlerts.danger(query.meta.errorMessage);
    }
    query.meta?.onError?.(error, query);
  },
});

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      /**
       * Fail immediately! TODO: Change this later based on all your
       * learnings about React Query.
       */
      retry: false,
      /**
       * The duration until inactive queries will be removed from the cache by garbage collector.
       * It should be set as the same value or higher than persistQueryClient's maxAge option.
       * E.g. if maxAge is 24 hours (the default) then gcTime should be 24 hours or higher.
       * If lower than maxAge, garbage collection will kick in and discard the stored cache earlier than expected.
       */
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      /**
       * The duration until a query transitions from fresh to stale.
       * As long as the query is fresh, data will always be read from the cache only - no network request will happen!
       * This default value can be overridden on a per-query basis when necessary.
       */
      // keep the line below commented until we write docs and an eslint rule to make sure people add staleTime to their queries
      // staleTime: 1000 * 60 * 60 * 24, // 24 hours
      refetchOnWindowFocus: false,
    },
  },
  queryCache,
});

const TRPCWrapper = ({ children }: { children: React.ReactNode }) => {
  const auth = useAuth();

  const endpoint =
    import.meta.env.MODE === "production"
      ? RPC_SERVER_ENDPOINTS.remote
      : RPC_SERVER_ENDPOINTS.local;
  /**
   * This URL length limit should be just less than AWS Cloudfront Limit. This
   * warns the user even if they chose `skipBatch: true`, and the setting keeps
   * batches below this total size, so large batches will be split. A single
   * "batched" request with a URL larger than this might still result in a HTTP
   * 413 error.
   * https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/cloudfront-limits.html#limits-general
   */
  const maxUrlLength = 8100;
  const trpcClient = useMemo(
    () =>
      trpc.createClient({
        /**
         * This setup/array is very cool and is Node as Fuck ✨♥️ I LOVE IT. You
         * create all these async streams that can log, retrieve, relay
         * telemetry, persist to browser storage, all right here 🚀
         *
         * NOTE: 👉👉👉 If the fact that this is an array has not clicked, ORDER
         * IS IMPORTANT HERE!
         */
        links: [
          /**
           * We may not need this level of logging in the browser console but I
           * know that Nick Galloway would've loved this 🥲
           */
          loggerLink({
            enabled: (opts) =>
              (import.meta.env.MODE !== "production" &&
                globalThis.window !== undefined) ||
              /**
               * From the docs: "The function passed to enabled is an example in
               * case you want to the link to log to your console in development
               * and only log errors in production."
               */
              (opts.direction === "down" && opts.result instanceof Error),
          }),

          /**
           * We use a `splitLink` because we want clients to be able to 'break
           * out' of batch-mode as and when they please. For example, this would
           * be used for known, slow requests so that their fetching does not
           * block UI rendering.
           *
           * To use this on the client, you would pass a context object that
           * sets `skipBatch` to `true`.
           */
          splitLink({
            condition: (op) => {
              try {
                const inputPayload = encodeURIComponent(JSON.stringify(op));
                if (inputPayload.length >= maxUrlLength) {
                  throw new Error(
                    `This tRPC input for ${op.path} is ${inputPayload.length} bytes, which is larger than limit of: ${maxUrlLength}.`,
                  );
                }
              } catch (error) {
                console.warn(
                  `${String(error)} This will likely result in a HTTP 413 error.`,
                );
              }
              return op.context.skipBatch === true;
            },
            true: httpLink({
              url: endpoint,
              /**
               * superjson lets us send Maps, Sets, and (importantly) Datetime
               * objects over the wire instead of serde-ing them in JSON. In
               * tRPC v11 the transformer is set per link.
               *
               * NOTE: If you ever remove this, remove it in the server as well!
               */
              transformer: superjson,
              /**
               * NOTE: This will be called with every request because it's a
               * function. Same with the `httpBatchLink` below.
               */
              headers: async () => {
                const token = await auth.getAccessToken();
                return { Authorization: `Bearer ${token}` };
              },
            }),
            false: httpBatchLink({
              url: endpoint,
              /**
               * superjson lets us send Maps, Sets, and (importantly) Datetime
               * objects over the wire instead of serde-ing them in JSON. In
               * tRPC v11 the transformer is set per link.
               *
               * NOTE: If you ever remove this, remove it in the server as well!
               */
              transformer: superjson,
              maxURLLength: maxUrlLength,
              headers: async () => {
                const token = await auth.getAccessToken();
                return { Authorization: `Bearer ${token}` };
              },
            }),
          }),
        ],
      }),
    [endpoint, auth],
  );

  /**
   * This is the configuration for how we persist the query cache between
   * sessions and visits, this way we can display data to the user even if
   * they unload and reload the app. Using this properly requires settings
   * `staleTime` on all of your queries. Please discuss with Tim Bendt if
   * you really believe we don't need this.
   */
  const { "use-robust-query-persister": useRobustQueryPersister = false } =
    useFeatureFlags();
  /**
   * Memoized because a persister is stateful: it holds the pending write it is
   * coalescing and subscribes to `pagehide` to flush it. A fresh one per render
   * would leak a listener per render.
   */
  const IDBPersister = useMemo(
    () =>
      useRobustQueryPersister
        ? createRobustIDBPersister()
        : createOriginalIDBPersister(),
    [useRobustQueryPersister],
  );

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister: IDBPersister,
        /**
         * This token is not "fresh" enough to be used for API calls, but when the real token
         * updates, this will follow suit quickly enough to be used for cache busting.
         *
         * While imperfect, it's usable as a react param (unlike the async getAccessToken function)
         */
        buster: auth.renderingToken,
        /**
         * Do NOT dehydrate (persist) mutations to IndexedDB.
         *
         * Why: Functions and Promises cannot be cloned, so any mutation whose `variables` object
         * contains a callback function will throw a `DataCloneError` when the persister tries to
         * write the cache.
         *
         * Root cause: Many mutation hooks across this codebase accept callback functions as part
         * of their mutation variables object (e.g. `mutate({ operationId, callback:
         * (success) => ... })`). This is an anti-pattern — callbacks should never be part of
         * mutation variables because React Query stores variables in the mutation cache, which is
         * then subject to serialization.
         *
         *   // ✅ Correct — callbacks are not stored in the cache
         *   mutate({ operationId }, { onSuccess: (data) => ..., onError: (err) => ... });
         *
         *   // ❌ Anti-pattern — callback ends up in the mutation cache and causes DataCloneError
         *   mutate({ operationId, callback: (success) => ... });
         *
         * Until all mutation hooks are refactored to use the correct pattern, we must exclude
         * mutations from persistence entirely. This is safe because we do not rely on offline
         * mutation replay — mutations are fire-and-forget.
         */
        dehydrateOptions: {
          shouldDehydrateMutation: () => false,
        },
      }}
      onSuccess={() => {
        addRumTiming("query_cache_restored");
        console.debug("Success restoring from cache");
      }}
    >
      <trpc.Provider client={trpcClient} queryClient={queryClient}>
        {children}
      </trpc.Provider>
    </PersistQueryClientProvider>
  );
};

const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <TRPCWrapper>{children}</TRPCWrapper>
);

export default Wrapper;
